import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtDate } from "@/lib/format";
import { MapPin, Package, Layers, Clock, ChevronRight, Loader2, PackageCheck } from "lucide-react";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  return "Good Evening";
}

const ACTIVE = ["Assigned", "Accepted", "In Transit", "Arrived"];

export default function DriverHome() {
  const navigate = useNavigate();
  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["driver-orders"],
    queryFn: async () => (await api.get("/orders")).data,
    refetchInterval: 15000,
  });

  const pending = orders.filter((o) => ACTIVE.includes(o.status));
  const doneToday = orders.filter((o) => ["Completed", "Partial Delivery", "Failed Delivery"].includes(o.status)).length;

  return (
    <div className="animate-slide-up">
      <p className="text-sm text-slate-500">{greeting()},</p>
      <h1 className="font-head text-2xl font-extrabold text-[#0B192C]">Here's your run sheet</h1>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-[#0B192C] p-4 text-white">
          <div className="text-3xl font-black">{pending.length}</div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-300">Pending Drops</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-3xl font-black text-emerald-600">{doneToday}</div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Completed</div>
        </div>
      </div>

      <h2 className="mb-2 mt-6 text-xs font-bold uppercase tracking-widest text-slate-400">Pending Deliveries</h2>

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#FF6500]" /></div>
      ) : pending.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-12 text-center">
          <PackageCheck className="mx-auto text-emerald-500" size={40} />
          <p className="mt-2 font-semibold text-[#0B192C]">All caught up!</p>
          <p className="text-sm text-slate-400">No pending deliveries right now.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pending.map((o) => (
            <button
              key={o.id}
              data-testid={`driver-delivery-card-${o.order_number}`}
              onClick={() => navigate(`/driver/delivery/${o.id}`)}
              className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm tap-shrink"
            >
              <div className="flex items-start justify-between">
                <span className="font-mono text-sm font-bold text-[#FF6500]">#{o.order_number}</span>
                <StatusBadge status={o.status} />
              </div>
              <div className="mt-1.5 font-head text-lg font-bold text-[#0B192C]">{o.customer_snapshot.name}</div>
              <div className="mt-0.5 flex items-start gap-1.5 text-sm text-slate-500">
                <MapPin size={15} className="mt-0.5 shrink-0" /> {o.customer_snapshot.delivery_address}
              </div>
              <div className="mt-3 flex items-center gap-4 text-sm text-slate-600">
                <span className="flex items-center gap-1"><Layers size={15} /> {o.pallets} Pallets</span>
                <span className="flex items-center gap-1"><Package size={15} /> {o.cartons} Cartons</span>
                <span className="flex items-center gap-1"><Clock size={15} /> {fmtDate(o.delivery_date, { day: "numeric", month: "short" })}</span>
              </div>
              <div className="mt-3 flex items-center justify-end text-sm font-bold text-[#0B192C]">
                Open delivery <ChevronRight size={18} />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
